import type { Session, DecodedToken } from '@nhost/nhost-js/session'
import { SESSION_COOKIE } from './session-cookie'

/**
 * Cookie codec for the Nhost session, shared by the browser storage (lib/nhost-cookie-storage.ts),
 * the server client (lib/nhost-server.ts), and the edge proxy (proxy.ts).
 *
 * WHY THIS EXISTS: the SDK's built-in CookieStorage serializes the WHOLE Session — including its
 * `decodedToken` field, which is a fully-expanded copy of the JWT payload. For a multi-property admin
 * (x-hasura-property-id / all-tenant-id arrays), the access token + that duplicated decoded copy,
 * URL-encoded, exceeds the browser's 4 KB per-cookie limit, so the browser silently drops the cookie
 * and the session never persists (client AND server see "logged out" → /dashboard ⇄ /login loop).
 *
 * FIX: persist the session WITHOUT the redundant `decodedToken` (~2.9 KB, fits) and rebuild it on
 * read. `decodeSessionToken` is a faithful replica of the SDK's own decoder — `exp`/`iat` are
 * converted to milliseconds (the SDK's refreshSession compares `decodedToken.exp` to `Date.now()`)
 * and Postgres-array claims are converted to JS arrays — so the SDK behaves identically.
 *
 * Only side-effect-free code lives here (no `document`, no browser SDK value imports), so the edge
 * proxy can import it without pulling the browser bundle into the edge runtime.
 */

export { SESSION_COOKIE }

const NS = 'https://hasura.io/jwt/claims'

function base64UrlDecode(part: string): string {
  let b64 = part.replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64.length % 4
  if (pad) b64 += '='.repeat(4 - pad)
  const bin = atob(b64)
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

function isPgArrayLiteral(v: string): boolean {
  return v.startsWith('{') && v.endsWith('}')
}

function parsePgArrayLiteral(v: string): string[] {
  return v && v !== '{}'
    ? v.slice(1, -1).split(',').map((x) => x.trim().replace(/^"(.*)"$/, '$1'))
    : []
}

/** Faithful replica of the SDK's decodeUserSession (see module doc). */
export function decodeSessionToken(accessToken: string): DecodedToken {
  const raw = JSON.parse(base64UrlDecode(accessToken.split('.')[1] ?? '')) as Record<string, unknown>
  const iat = typeof raw.iat === 'number' ? 1000 * raw.iat : undefined
  const exp = typeof raw.exp === 'number' ? 1000 * raw.exp : undefined
  const claims = raw[NS] as Record<string, unknown> | undefined
  const decodedClaims = claims
    ? Object.entries(claims).reduce<Record<string, unknown>>((acc, [k, v]) => {
        acc[k] = typeof v === 'string' && isPgArrayLiteral(v) ? parsePgArrayLiteral(v) : v
        return acc
      }, {})
    : undefined
  return { ...raw, iat, exp, [NS]: decodedClaims } as DecodedToken
}

/** Serialize a session for the cookie: drop the bulky, derivable `decodedToken`, then URL-encode. */
export function serializeSession(session: Session): string {
  const { decodedToken: _omit, ...rest } = session as Session & { decodedToken?: unknown }
  return encodeURIComponent(JSON.stringify(rest))
}

/**
 * Parse a cookie value back into a full Session (with decodedToken rebuilt). Handles both URL-encoded
 * (browser/CookieStorage) and already-decoded values. Returns null if unusable.
 */
export function deserializeSession(rawCookieValue: string): Session | null {
  let parsed: (Partial<Session> & { accessToken?: string }) | null = null
  try {
    parsed = JSON.parse(decodeURIComponent(rawCookieValue))
  } catch {
    try {
      parsed = JSON.parse(rawCookieValue)
    } catch {
      return null
    }
  }
  if (!parsed?.accessToken) return null
  return { ...(parsed as Session), decodedToken: decodeSessionToken(parsed.accessToken) }
}
